import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Button, TextField, CircularProgress, Alert, IconButton } from "@mui/material";
import { Save, Add, Delete } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import systemSettingsService from "../../services/systemSettingsService";
import PageHeader from "../../components/common/PageHeader";

const SAMPLE_DATA = {
    companyName: "Waypoint Imports",
    officeAddress: "20 Windward Dr, Savannah, Grand Cayman",
    contactEmail: "info@waypointimports.com",
    contactPhone: "345-000-0000",
    invoiceNumber: "INV-2026-0042",
    status: "sent",
    customerName: "Samama Inam",
    customerAddress: "20 Windward Dr, Savannah, Grand Cayman",
    customerEmail: "customer@example.com",
    issueDate: "7-25-2026",
    dueDate: "8-8-2026",
    shipmentTrackingNumbers: "WP-2026-0024",
    itemRows: `<tr><td style="padding:6px 8px;border-bottom:1px solid #eee">Bluetooth Speaker</td><td style="padding:6px 8px;border-bottom:1px solid #eee;font-family:monospace">WP-2026-0024</td><td style="padding:6px 8px;border-bottom:1px solid #eee;font-family:monospace">1Z999AA10123456784</td><td style="text-align:right;padding:6px 8px;border-bottom:1px solid #eee">1</td><td style="text-align:right;padding:6px 8px;border-bottom:1px solid #eee">$45.00</td></tr>`,
    lineRows: `<tr><td style="padding:6px 8px;border-bottom:1px solid #eee">Freight &amp; Handling</td><td style="text-align:right;padding:6px 8px;border-bottom:1px solid #eee">1</td><td style="text-align:right;padding:6px 8px;border-bottom:1px solid #eee">$120.00</td><td style="text-align:right;padding:6px 8px;border-bottom:1px solid #eee">$120.00</td></tr>`,
    subtotal: "120.00",
    taxAmount: "0.00",
    dutyAmount: "15.00",
    totalAmount: "135.00",
    amountPaid: "0.00",
    balanceDue: "135.00",
    notesBlock: `<div style="padding:12px 20px;border-top:1px solid #ddd"><div style="font-size:10px;color:#666;text-transform:uppercase;font-weight:700;margin-bottom:4px">Notes</div><div>Thank you for your business.</div></div>`,
    bankDetails: `<div style="padding:16px 20px;border-top:1px solid #ddd;background:#f9fafb"><div style="font-size:10px;color:#666;text-transform:uppercase;font-weight:700;margin-bottom:6px">Bank Details</div><div style="font-weight:600">Waypoint Imports</div><div>10501152 - USD Chequing</div><div>10501153 - KYD Chequing</div><div>CIBC Caribbean</div></div>`,
};

const renderPreview = (html, data) =>
    (html || "").replace(/\{\{\s*([\w]+)\s*\}\}/g, (m, key) =>
        Object.prototype.hasOwnProperty.call(data, key) ? data[key] : m,
    );

const AdminInvoiceTemplate = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [html, setHtml] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [bankDetails, setBankDetails] = useState({ accountHolder: "", bankName: "", accounts: [] });
    const [savingBank, setSavingBank] = useState(false);

    useEffect(() => {
        systemSettingsService
            .getInvoiceTemplate()
            .then(({ data }) => setHtml(data.data.templateHtml || ""))
            .catch(() => enqueueSnackbar("Failed to load template", { variant: "error" }))
            .finally(() => setLoading(false));
        systemSettingsService
            .getBankDetails()
            .then(({ data }) =>
                setBankDetails({
                    accountHolder: data.data.bankDetails?.accountHolder || "",
                    bankName: data.data.bankDetails?.bankName || "",
                    accounts: data.data.bankDetails?.accounts || [],
                }),
            )
            .catch(() => enqueueSnackbar("Failed to load bank details", { variant: "error" }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            await systemSettingsService.update({ invoiceTemplateHtml: html });
            enqueueSnackbar("Template saved", { variant: "success" });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to save template", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    const updateAccount = (idx, field) => (e) =>
        setBankDetails((p) => ({
            ...p,
            accounts: p.accounts.map((a, i) => (i === idx ? { ...a, [field]: e.target.value } : a)),
        }));
    const addAccount = () =>
        setBankDetails((p) => ({ ...p, accounts: [...p.accounts, { label: "", number: "" }] }));
    const removeAccount = (idx) =>
        setBankDetails((p) => ({ ...p, accounts: p.accounts.filter((_, i) => i !== idx) }));

    const handleSaveBankDetails = async () => {
        setSavingBank(true);
        try {
            await systemSettingsService.update({ bankDetails });
            enqueueSnackbar("Bank details saved", { variant: "success" });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to save bank details", { variant: "error" });
        } finally {
            setSavingBank(false);
        }
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader title="Invoice Template" subtitle="Customize the HTML used for the printable/PDF invoice, including bank details" />

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                        Available variables: {"{{companyName}}"}, {"{{officeAddress}}"}, {"{{contactEmail}}"}, {"{{contactPhone}}"},{" "}
                        {"{{invoiceNumber}}"}, {"{{status}}"}, {"{{customerName}}"}, {"{{customerAddress}}"}, {"{{customerEmail}}"},{" "}
                        {"{{issueDate}}"}, {"{{dueDate}}"}, {"{{shipmentTrackingNumbers}}"}, {"{{itemRows}}"}, {"{{lineRows}}"}, {"{{subtotal}}"},{" "}
                        {"{{taxAmount}}"}, {"{{dutyAmount}}"}, {"{{totalAmount}}"}, {"{{amountPaid}}"}, {"{{balanceDue}}"},{" "}
                        {"{{notesBlock}}"}, {"{{itemCount}}"}, {"{{bankDetails}}"}
                    </Alert>

                    <Card>
                        <CardContent>
                            <Typography variant="overline" color="text.secondary" display="block" mb={1.5}>
                                Bank Details
                            </Typography>
                            <Typography variant="body2" color="text.secondary" mb={2}>
                                Shown on the printed invoice ({"{{bankDetails}}"} above) and in the customer's "Pay
                                Now" dialog when they choose Bank Transfer.
                            </Typography>
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mb: 2 }}>
                                <TextField
                                    label="Account Holder"
                                    value={bankDetails.accountHolder}
                                    onChange={(e) => setBankDetails((p) => ({ ...p, accountHolder: e.target.value }))}
                                    size="small"
                                />
                                <TextField
                                    label="Bank Name"
                                    value={bankDetails.bankName}
                                    onChange={(e) => setBankDetails((p) => ({ ...p, bankName: e.target.value }))}
                                    size="small"
                                />
                            </Box>
                            {bankDetails.accounts.map((acc, idx) => (
                                <Box key={idx} sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
                                    <TextField
                                        label="Account Number"
                                        value={acc.number}
                                        onChange={updateAccount(idx, "number")}
                                        size="small"
                                        sx={{ flex: 1 }}
                                    />
                                    <TextField
                                        label="Label"
                                        placeholder="e.g. USD Chequing"
                                        value={acc.label}
                                        onChange={updateAccount(idx, "label")}
                                        size="small"
                                        sx={{ flex: 1 }}
                                    />
                                    <IconButton size="small" onClick={() => removeAccount(idx)}>
                                        <Delete fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mt: 1 }}>
                                <Button size="small" startIcon={<Add />} onClick={addAccount}>
                                    Add Account
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<Save />}
                                    onClick={handleSaveBankDetails}
                                    disabled={savingBank}
                                >
                                    {savingBank ? <CircularProgress size={16} color="inherit" /> : "Save Bank Details"}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>

                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3 }}>
                        <Card>
                            <CardContent>
                                <TextField
                                    label="HTML Template"
                                    value={html}
                                    onChange={(e) => setHtml(e.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={24}
                                    sx={{ "& textarea": { fontFamily: "monospace", fontSize: 12.5 } }}
                                />
                                <Button
                                    variant="contained"
                                    startIcon={<Save />}
                                    onClick={handleSave}
                                    disabled={saving}
                                    sx={{ mt: 2 }}
                                >
                                    {saving ? <CircularProgress size={18} color="inherit" /> : "Save Template"}
                                </Button>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardContent>
                                <Typography variant="overline" color="text.secondary" display="block" mb={1.5}>
                                    Preview (sample data)
                                </Typography>
                                {/* Always a plain white page with dark text, regardless of
                                    the app's own light/dark mode — this previews what will
                                    actually PRINT, which is always white paper, never a dark
                                    page. The injected template HTML itself assumes a white
                                    backdrop (it has no background of its own), so leaving this
                                    to follow the app theme meant dark-on-dark/invisible text
                                    in dark mode. */}
                                <Box
                                    sx={{
                                        border: "1px solid #ddd",
                                        borderRadius: 2,
                                        overflow: "hidden",
                                        bgcolor: "#fff",
                                        color: "#0E1420",
                                    }}
                                    dangerouslySetInnerHTML={{ __html: renderPreview(html, SAMPLE_DATA) }}
                                />
                            </CardContent>
                        </Card>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default AdminInvoiceTemplate;
